/**
 * Sezione «Template» (Impostazioni) — guidata dal REGIME.
 *
 * Per il regime scelto mostra la lista attesa dei documenti (da SetDocumenti)
 * come slot: ogni slot ha lo stato «template caricato / manca», il caricamento
 * del `.docx` a livello regime (default) e una sotto-sezione di override per
 * singolo paese. Il manifest nasce pre-mappato dal default del tipo.
 */

import {
  AlertTriangle,
  Check,
  ChevronDown,
  Download,
  FileText,
  History,
  Loader2,
  Plus,
  Upload,
} from "lucide-react";
import { useRef, useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import {
  useM3Paesi,
  useM3TemplateAttiva,
  useM3TemplateDettaglio,
  useM3TemplateManifest,
  useM3TemplatePanoramica,
  useM3TemplateUpload,
  useM3TemplateVersioni,
} from "@/hooks/useM3";
import { apiDownloadFile } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { TemplateDetail, TemplateRead, TemplateSlot } from "@/types/api";

const INPUT =
  "h-9 rounded-md border bg-card px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

const REGIMI: { value: string; label: string }[] = [
  { value: "bielorussia", label: "Bielorussia / Ex-URSS" },
  { value: "extra_ue", label: "Extra-UE" },
  { value: "ue", label: "UE" },
  { value: "italia", label: "Italia" },
];

export function TemplateSection() {
  const [regime, setRegime] = useState("bielorussia");
  const { data, isLoading } = useM3TemplatePanoramica(regime);

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-foreground">Template documenti</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          I documenti previsti dal <b>regime</b> sono elencati come slot: carica il{" "}
          <code>.docx</code> a livello regime (vale per tutti i suoi paesi) e, dove serve,
          un override per singolo paese. Il manifest nasce pre-mappato dal tipo di documento.
        </p>
      </div>

      {/* Selettore regime */}
      <div className="flex flex-wrap gap-1.5">
        {REGIMI.map((r) => (
          <button
            key={r.value}
            type="button"
            onClick={() => setRegime(r.value)}
            className={cn(
              "rounded-md border px-3 py-1.5 text-xs font-semibold",
              regime === r.value
                ? "border-brand-300 bg-brand-50 text-brand-700"
                : "bg-card text-muted-foreground hover:bg-muted",
            )}
          >
            {r.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !data || data.slots.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nessun documento generato previsto per questo regime. Configura il set in
          «Paesi & Regimi».
        </p>
      ) : (
        <div className="space-y-2">
          {data.slots.map((s) => (
            <SlotCard key={s.tipo_documento} slot={s} regime={regime} />
          ))}
        </div>
      )}
    </section>
  );
}

function SlotCard({ slot, regime }: { slot: TemplateSlot; regime: string }) {
  const [aperto, setAperto] = useState(false);
  const [overrideAperto, setOverrideAperto] = useState(false);
  const upload = useM3TemplateUpload();
  const fileRef = useRef<HTMLInputElement>(null);
  const caricato = slot.regime_template != null;

  function caricaRegime(file: File) {
    upload.mutate({ file, tipo_documento: slot.tipo_documento, regime });
  }

  return (
    <div className="rounded-lg border">
      <button
        type="button"
        onClick={() => setAperto((a) => !a)}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-muted/40"
      >
        <FileText className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">{slot.label}</span>
        {slot.lingue?.length ? (
          <span className="text-[11px] text-muted-foreground">
            {slot.lingue.join(", ").toUpperCase()}
          </span>
        ) : null}
        <span className="ml-auto flex items-center gap-2">
          {caricato ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
              <Check className="h-3 w-3" /> Template v{slot.regime_template!.versione}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
              <AlertTriangle className="h-3 w-3" /> Manca
            </span>
          )}
          {slot.override_paesi.length > 0 && (
            <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
              {slot.override_paesi.length} override
            </span>
          )}
          <ChevronDown
            className={cn("h-4 w-4 text-muted-foreground transition-transform", aperto && "rotate-180")}
          />
        </span>
      </button>

      {aperto && (
        <div className="space-y-3 border-t bg-muted/20 px-3 py-3">
          {/* Template del regime */}
          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Template del regime (default)
            </div>
            {slot.regime_template ? (
              <TemplateDettaglio id={slot.regime_template.id} />
            ) : (
              <div className="flex items-center gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".docx"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) caricaRegime(f);
                    e.target.value = "";
                  }}
                />
                <button
                  type="button"
                  disabled={upload.isPending}
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {upload.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Upload className="h-3.5 w-3.5" />
                  )}
                  Carica .docx per il regime
                </button>
                <span className="text-[11px] text-muted-foreground">
                  Varrà per tutti i paesi del regime.
                </span>
              </div>
            )}
          </div>

          {/* Override per paese */}
          <div>
            <button
              type="button"
              onClick={() => setOverrideAperto((o) => !o)}
              className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
            >
              <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", overrideAperto && "rotate-180")} />
              Override per paese ({slot.override_paesi.length})
            </button>
            {overrideAperto && (
              <div className="mt-2 space-y-2">
                {slot.override_paesi.map((o) => (
                  <OverrideRow key={o.id} tpl={o} />
                ))}
                <AggiungiOverride tipo={slot.tipo_documento} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function OverrideRow({ tpl }: { tpl: TemplateRead }) {
  const [aperto, setAperto] = useState(false);
  return (
    <div className="rounded-md border bg-card">
      <button
        type="button"
        onClick={() => setAperto((a) => !a)}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs hover:bg-muted/40"
      >
        <span className="font-semibold">{tpl.paese_iso}</span>
        <span className="text-muted-foreground">v{tpl.versione}</span>
        <ChevronDown className={cn("ml-auto h-3.5 w-3.5 transition-transform", aperto && "rotate-180")} />
      </button>
      {aperto && <TemplateDettaglio id={tpl.id} />}
    </div>
  );
}

function AggiungiOverride({ tipo }: { tipo: string }) {
  const { data: paesi } = useM3Paesi();
  const upload = useM3TemplateUpload();
  const fileRef = useRef<HTMLInputElement>(null);
  const [paese, setPaese] = useState("");

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed p-2">
      <Plus className="h-3.5 w-3.5 text-muted-foreground" />
      <select className={INPUT} value={paese} onChange={(e) => setPaese(e.target.value)}>
        <option value="">— paese —</option>
        {(paesi ?? []).map((p) => (
          <option key={p.sigla} value={p.sigla}>
            {p.sigla} — {p.nome ?? "—"}
          </option>
        ))}
      </select>
      <input
        ref={fileRef}
        type="file"
        accept=".docx"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f && paese) {
            upload.mutate({ file: f, tipo_documento: tipo, paese_iso: paese });
            setPaese("");
          }
          e.target.value = "";
        }}
      />
      <button
        type="button"
        disabled={!paese || upload.isPending}
        onClick={() => fileRef.current?.click()}
        className="inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs hover:bg-muted disabled:opacity-50"
      >
        {upload.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Upload className="h-3.5 w-3.5" />
        )}
        Carica override
      </button>
    </div>
  );
}

function TemplateDettaglio({ id }: { id: string }) {
  const { data: det, isLoading } = useM3TemplateDettaglio(id);
  if (isLoading || !det) {
    return (
      <div className="px-3 pb-3">
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  return <DettaglioCorpo det={det} />;
}

function DettaglioCorpo({ det }: { det: TemplateDetail }) {
  const salvaManifest = useM3TemplateManifest(det.id);
  const reupload = useM3TemplateUpload();
  const fileRef = useRef<HTMLInputElement>(null);
  const [json, setJson] = useState(() => JSON.stringify(det.manifest ?? {}, null, 2));
  const [jsonErr, setJsonErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [storico, setStorico] = useState(false);

  const diff = det.placeholder_diff;
  const haDiff =
    diff.nel_docx_non_mappati.length > 0 || diff.nel_manifest_non_nel_docx.length > 0;

  async function onSalva() {
    setJsonErr(null);
    setMsg(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch {
      setJsonErr("JSON non valido.");
      return;
    }
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      !Array.isArray((parsed as { segnaposto?: unknown }).segnaposto)
    ) {
      setJsonErr("Il manifest deve avere una lista 'segnaposto'.");
      return;
    }
    try {
      await salvaManifest.mutateAsync({
        manifest: parsed as TemplateDetail["manifest"] & object,
      });
      setMsg("Manifest salvato (nuova versione).");
    } catch (e) {
      setJsonErr(e instanceof Error ? e.message : "Salvataggio fallito.");
    }
  }

  return (
    <div className="space-y-3 border-t bg-muted/20 px-3 py-3">
      <div className="flex flex-wrap items-center gap-2">
        {det.ha_asset && (
          <button
            type="button"
            onClick={() =>
              apiDownloadFile(
                `/api/v1/m3/template/${det.id}/asset`,
                det.nome_file_asset ?? "template.docx",
              )
            }
            className="inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs hover:bg-muted"
          >
            <Download className="h-3.5 w-3.5" /> Scarica .docx
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept=".docx"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f)
              reupload.mutateAsync({
                file: f,
                tipo_documento: det.tipo_documento,
                paese_iso: det.paese_iso,
                regime: det.regime,
              });
            e.target.value = "";
          }}
        />
        <button
          type="button"
          disabled={reupload.isPending}
          onClick={() => fileRef.current?.click()}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs hover:bg-muted disabled:opacity-50"
        >
          {reupload.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5" />
          )}
          Sostituisci .docx
        </button>
        <button
          type="button"
          onClick={() => setStorico((s) => !s)}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs hover:bg-muted"
        >
          <History className="h-3.5 w-3.5" /> Versioni
        </button>
      </div>

      {det.modalita === "template_word" && (
        <div className="text-xs">
          <span className="text-muted-foreground">Segnaposto nel .docx: </span>
          {det.placeholders.length ? (
            det.placeholders.map((p) => (
              <code key={p} className="mr-1 rounded bg-muted px-1">
                {p}
              </code>
            ))
          ) : (
            <span className="italic text-muted-foreground">nessuno</span>
          )}
          {haDiff && (
            <div className="mt-1.5 flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 p-2 text-amber-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <div>
                {diff.nel_docx_non_mappati.length > 0 && (
                  <div>
                    Nel .docx ma non nel manifest: <b>{diff.nel_docx_non_mappati.join(", ")}</b>
                  </div>
                )}
                {diff.nel_manifest_non_nel_docx.length > 0 && (
                  <div>
                    Nel manifest ma non nel .docx:{" "}
                    <b>{diff.nel_manifest_non_nel_docx.join(", ")}</b>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">
            Manifest (mappatura segnaposto → dati)
          </span>
          <button
            type="button"
            disabled={salvaManifest.isPending}
            onClick={onSalva}
            className="inline-flex h-7 items-center gap-1.5 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {salvaManifest.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            Salva manifest
          </button>
        </div>
        <textarea
          className="h-56 w-full rounded-md border bg-card p-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-ring"
          value={json}
          onChange={(e) => setJson(e.target.value)}
          spellCheck={false}
        />
        {jsonErr && <p className="mt-1 text-xs text-destructive">{jsonErr}</p>}
        {msg && <p className="mt-1 text-xs text-emerald-600">{msg}</p>}
      </div>

      {storico && <StoricoVersioni tipo={det.tipo_documento} paese={det.paese_iso} />}
    </div>
  );
}

function StoricoVersioni({ tipo, paese }: { tipo: string; paese: string | null }) {
  const { data: versioni, isLoading } = useM3TemplateVersioni(tipo, paese);
  const attiva = useM3TemplateAttiva();
  if (isLoading) return <Skeleton className="h-16 w-full" />;
  return (
    <div className="rounded-md border bg-card">
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr className="border-b">
            <th className="px-2 py-1 text-left">Versione</th>
            <th className="px-2 py-1 text-left">Stato</th>
            <th className="px-2 py-1 text-left">Note</th>
            <th className="px-2 py-1" />
          </tr>
        </thead>
        <tbody>
          {(versioni ?? []).map((v) => (
            <tr key={v.id} className="border-b last:border-0">
              <td className="px-2 py-1 tabular-nums">v{v.versione}</td>
              <td className="px-2 py-1">
                {v.attivo ? (
                  <span className="text-emerald-600">attiva</span>
                ) : (
                  <span className="text-muted-foreground">storico</span>
                )}
              </td>
              <td className="px-2 py-1 text-muted-foreground">{v.note_versione ?? "—"}</td>
              <td className="px-2 py-1 text-right">
                {!v.attivo && (
                  <button
                    type="button"
                    disabled={attiva.isPending}
                    onClick={() => attiva.mutate(v.id)}
                    className="rounded border px-2 py-0.5 hover:bg-muted disabled:opacity-50"
                  >
                    Riattiva
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
